import { type HTMLMotionProps, motion } from 'motion/react'
import Image from 'next/image'
import Link from 'next/link'
import type { FC } from 'react'
import type { IMarkerData } from '../molecules/VenuesMap/types'
import { IconCircle } from './IconCircle'
import { PlaceholderImage } from './PlaceholderImage'

export type MapPopoverProps = HTMLMotionProps<'div'> & {
	data: IMarkerData
	selected: boolean
	onSelectVenue: (venueId: string | null) => void
}

const MapPopover: FC<MapPopoverProps> = ({ id, data, selected, onSelectVenue, ...props }) => {
	const { url, name, location, imageUrl, imageAlt, cardLabel } = data
	return (
		<motion.div
			id={`popover-${id}`}
			aria-labelledby={`marker-${id}, popover-${id}-close`}
			initial={{ opacity: 0, y: 10, pointerEvents: 'none' }}
			animate={
				selected
					? { opacity: 1, y: 0, pointerEvents: 'auto' }
					: { opacity: 0, y: 10, pointerEvents: 'none' }
			}
			className="absolute -top-2 left-1/2 -translate-y-full -translate-x-1/2 w-60"
			aria-hidden={!selected}
			{...props}
		>
			<button
				className="absolute -top-3.5 right-3 z-1 bg-blue rounded-full cursor-pointer group"
				onClick={() => onSelectVenue(null)}
				aria-label="Close popover"
				aria-expanded={selected}
				aria-controls={`popover-${id}`}
				id={`popover-${id}-close`}
			>
				<IconCircle icon={['fas', 'close']} size="sm" intent="primary" theme="white" />
			</button>
			<Link
				href={url}
				target="_self"
				className="relative block rounded-sm overflow-hidden group shadow-xl"
			>
				<div className="relative aspect-video w-full overflow-hidden">
					{imageUrl ? (
						<Image
							src={imageUrl}
							alt={imageAlt ?? ''}
							fill
							className="object-cover group-hover:scale-110 transition-transform ease-in-out duration-300"
							sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, (max-width: 1559px) 66.67vw, 456px"
						/>
					) : (
						<PlaceholderImage />
					)}
				</div>

				<div className="p-xl bg-blue-tint-10 group-hover:bg-blue-tint-20 transition-colors ease-in-out duration-300">
					<h5 className="text-h5 text-blue group-hover:text-blue-shade-40 group-active:text-blue-shade-70 transition-colors ease-in-out duration-300">
						{name}
					</h5>
					<p className="text-h6 text-blue-shade-70 mt-xs">{location}</p>
					{cardLabel && <div className="mt-sm text-body-xs">{cardLabel}</div>}
				</div>
			</Link>
		</motion.div>
	)
}

export default MapPopover
